import { useCallback, useState } from "react"
import type { DealDrawer as DealDrawerData } from "@/types/v2/conversation"
import { getCsrfToken } from "@/lib/csrf"
import { replaceDealProp } from "@/lib/deal-prop-update"
import type { DealPropKey } from "@/lib/deal-prop-update"

// Mutation hook for the deal CONVERSATIONS list (CON-261).
// Mirrors `useDealContacts`: a single optimistic action that
// patches the `deal` prop, fires a fetch, and reverts on
// 422 / network failure. The host page's ActionCable
// subscription reconciles any stale fields when the server
// confirms.

interface UseDealConversationsOptions {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}

interface UseDealConversationsResult {
  // Detach a linked conversation from this deal. Only
  // `unlinkable` rows can be removed (the source thread can't).
  unlink: (conversationId: number) => void
  // True while the unlink request is in-flight.
  submitting: boolean
}

export function useDealConversations({
  deal,
  dealPropKey,
}: UseDealConversationsOptions): UseDealConversationsResult {
  const [submitting, setSubmitting] = useState(false)

  const unlink = useCallback(
    (conversationId: number) => {
      if (submitting) return
      const conversations = deal.conversations ?? []
      const target = conversations.find((c) => c.id === conversationId)
      // Guard: the source thread (and any non-unlinkable row)
      // can't be detached. The backend also enforces this.
      if (!target || !target.unlinkable) return

      const optimistic: DealDrawerData = {
        ...deal,
        conversations: conversations.filter(
          (c) => c.id !== conversationId,
        ),
      }

      replaceDealProp(dealPropKey, () => optimistic)
      setSubmitting(true)
      fetch(`/v2/deals/${deal.id}/conversations/${conversationId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
        })
        .catch(() => {
          replaceDealProp(dealPropKey, () => deal)
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
    [deal, dealPropKey, submitting],
  )

  return { unlink, submitting }
}
